/**
 * ClaimUI — pre-game setup phase. Tap an unclaimed territory (or once
 * everything is owned, one of your own) to place 1 starting army.
 *
 * Layout is strictly stacked so overlays never cover the map: the player
 * legend + remaining-armies HUD sit ABOVE the full-width map.
 */

import React, { useMemo, useCallback, useRef } from 'react'
import { View, Text, ActivityIndicator } from 'react-native'
import { MapView } from '../map/MapView'
import { TurnControlCard } from './TurnControlCard'
import { MapControlsBarSlot } from '../map/MapControlsBar'
import {
  useGameMapSceneRegistration,
  type GameMapSceneRegistration,
} from '../map/gameMapScene'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { useClaim } from '../../hooks/useGames'
import { buildClaimDeduplicate } from '../../lib/claimGuard'
import type { GameState } from '../../lib/gameState'
import type { GamePlayerResponse } from '@warcarp/api-types'
import type { PanOffset } from '../../lib/mapZoom'

interface ClaimUIProps {
  gameId: string
  state: GameState
  myPlayerId: string
  slotByPlayerId: Record<string, number>
  gamePlayers: GamePlayerResponse[]
  viewerUserId?: string
  /** Forwarded to MapView so the player-roster strip pins to the map's
   * right edge and centres on its midpoint (not the screen's). */
  mapRightOverlay?: React.ReactNode
  regionToggleNode?: React.ReactNode
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode?: React.ReactNode
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Optional header row rendered at the TOP of the landscape action
   * panel (above the HUD). The game screen uses this to colocate the
   * landscape cog + horizontal player-roster strip there, so neither
   * floats over the map (Dynamic Island can no longer obscure them).
   * Portrait keeps its existing top header bar — this prop is a no-op
   * in portrait orientation.
   */
  actionPanelHeader?: React.ReactNode
  /** Thin control bar rendered directly beneath the map (zoom + view mode). */
  mapControlsBar?: React.ReactNode
  /** Forwarded to MapView — zoom-and-pan state managed by the game screen. */
  zoomEnabled?: boolean
  panOffset?: PanOffset
  onPanChange?: (offset: PanOffset) => void
  zoomScale?: number
  onZoomScaleChange?: (scale: number) => void
  /** Forwarded to MapView for bot-attack animation fix (see useAttackEvents). */
  viewerJoinedAt?: number
  /** Time-machine hook — scrubs the game screen into the snapshot view at the given event index. */
  onViewAtEvent?: (eventIndex: number) => void
  selectedEventIndex?: number | null
  /** When set, the parent owns the single shared MapView. */
  mapSceneKey?: string
  onMapSceneConfigChange?: GameMapSceneRegistration
}

export function ClaimUI({
  gameId,
  state,
  myPlayerId,
  slotByPlayerId,
  gamePlayers,
  viewerUserId,
  mapRightOverlay,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  actionPanelHeader,
  mapControlsBar,
  zoomEnabled,
  panOffset,
  onPanChange,
  zoomScale,
  onZoomScaleChange,
  viewerJoinedAt,
  mapSceneKey,
  onMapSceneConfigChange,
}: ClaimUIProps) {
  const claim = useClaim(gameId)
  const viewerSlot = slotByPlayerId[myPlayerId] ?? 1

  // Deduplicator: blocks a second tap on the same territory within a 3-second
  // window. Prevents "You already own 'X'" server rejections (WARCARP-APP-D/E)
  // that fire when the client submits a claim for a territory that is still
  // shown as valid in the stale local cache but has already been claimed.
  // A stable ref so it survives re-renders without resetting its in-flight map.
  const dedupRef = useRef(buildClaimDeduplicate())

  const myRemaining = state.armiesToPlace[myPlayerId] ?? 0
  const anyUnclaimed = useMemo(
    () => state.map.territories.some((t) => !t.ownerId),
    [state.map.territories],
  )

  const validTargetIds = useMemo(() => {
    if (anyUnclaimed) {
      return state.map.territories.filter((t) => !t.ownerId).map((t) => t.id)
    }
    return state.map.territories.filter((t) => t.ownerId === myPlayerId).map((t) => t.id)
  }, [state.map.territories, anyUnclaimed, myPlayerId])

  const handleTerritoryPress = useCallback((territoryId: string) => {
    if (claim.isPending) return
    if (myRemaining <= 0) return
    if (!validTargetIds.includes(territoryId)) return
    // Short-circuit rapid double-taps before they reach the network.
    if (dedupRef.current(territoryId)) return
    claim.mutate({ territoryId })
  }, [claim, myRemaining, validTargetIds])

  useGameMapSceneRegistration({
    key: mapSceneKey,
    onChange: onMapSceneConfigChange,
    config: {
      validTargetIds,
      onTerritoryPress: handleTerritoryPress,
    },
  })

  const currentSlot = parseInt(state.players[state.turnIndex] ?? '0', 10)

  const { isLandscape, isDesktop, actionPanelWidth } = useMapDimensions(state)
  const hud = (
    <TurnControlCard
      style={{ marginHorizontal: isDesktop ? 12 : 8, marginTop: 8, ...(isLandscape ? { width: actionPanelWidth - (isDesktop ? 24 : 16) } : null) }}
    >
      <View className="flex-row items-baseline gap-1">
        <Text
          className="text-2xl font-stencil text-ink"
          accessibilityLabel={`${myRemaining} starting armies remaining`}
        >
          {myRemaining}
        </Text>
        <Text className="text-ink-soft text-xs font-typed">left to place</Text>
      </View>
      <Text className="text-ink-soft text-[10px] mt-0.5 font-typed" numberOfLines={1}>
        {anyUnclaimed ? 'Pick a territory' : 'Tap one you own'}
      </Text>
      {claim.isPending && <ActivityIndicator size="small" color="#94a3b8" className="mt-1" />}
    </TurnControlCard>
  )

  if (mapSceneKey && onMapSceneConfigChange) {
    return isLandscape ? (
      <View style={{ width: actionPanelWidth }} className="flex-col">
        {actionPanelHeader}
        {hud}
      </View>
    ) : (
      hud
    )
  }

  return (
    <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
      {isLandscape && (
        <View style={{ width: actionPanelWidth }} className="flex-col">
          {actionPanelHeader}
          {hud}
        </View>
      )}
      {/* Landscape-only centring; portrait keeps map's explicit height
          so the HUD below gets its 220 px reserve. See AttackUI.
          Desktop pins the map flush to the top (matches the map
          explorer); phone landscape keeps vertical centring. */}
      {isLandscape ? (
        <View className="flex-1 border-l border-olive bg-paper">
          <View className={`${isDesktop ? 'items-start' : 'flex-1 items-start justify-center'}`}>
            <MapView
              state={state}
              viewerSlot={viewerSlot}
              validTargetIds={validTargetIds}
              onTerritoryPress={handleTerritoryPress}
              rightOverlay={mapRightOverlay}
              regionToggleNode={regionToggleNode}
              regionToggleCorner={regionToggleCorner}
              zoomToggleNode={zoomToggleNode}
              zoomToggleCorner={zoomToggleCorner}
              hideOnMapZoomButtons={!!mapControlsBar}
              reservedBottom={mapControlsBar ? 44 : undefined}
              zoomEnabled={zoomEnabled}
              panOffset={panOffset}
              onPanChange={onPanChange}
              zoomScale={zoomScale}
              onZoomScaleChange={onZoomScaleChange}
              viewerJoinedAt={viewerJoinedAt}
            />
          </View>
          <MapControlsBarSlot state={state}>{mapControlsBar}</MapControlsBarSlot>
        </View>
      ) : (
        <MapView
          state={state}
          viewerSlot={viewerSlot}
          validTargetIds={validTargetIds}
          onTerritoryPress={handleTerritoryPress}
          rightOverlay={mapRightOverlay}
          regionToggleNode={regionToggleNode}
          regionToggleCorner={regionToggleCorner}
          zoomToggleNode={zoomToggleNode}
          zoomToggleCorner={zoomToggleCorner}
          hideOnMapZoomButtons={!!mapControlsBar}
          zoomEnabled={zoomEnabled}
          panOffset={panOffset}
          onPanChange={onPanChange}
          zoomScale={zoomScale}
          onZoomScaleChange={onZoomScaleChange}
          viewerJoinedAt={viewerJoinedAt}
        />
      )}
      {!isLandscape && mapControlsBar}
      {!isLandscape && hud}
    </View>
  )
}
